import React from "react";
import { Loader2 } from "lucide-react";
import GlassCard from "@/components/common/GlassCard";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { followUpView, pageDisplayName, pageRowStatus, searchRowStatus } from "@/components/seo/seoUtils";

export function SEORobotsManager({ data, onSave }) {
  const [groups, setGroups] = React.useState(data?.groups || [{ user_agent: "*", rules: [{ type: "allow", path: "/" }] }]);
  const [sitemaps, setSitemaps] = React.useState((data?.sitemaps || []).join("\n"));
  const [raw, setRaw] = React.useState(data?.raw || "");
  const [mode, setMode] = React.useState("visual");
  React.useEffect(() => {
    setGroups(data?.groups?.length ? data.groups : [{ user_agent: "*", rules: [{ type: "allow", path: "/" }] }]);
    setSitemaps((data?.sitemaps || []).join("\n"));
    setRaw(data?.raw || "");
  }, [data]);
  const setGroup = (i, next) => setGroups((g) => g.map((row, idx) => (idx === i ? next : row)));
  return (
    <GlassCard className="p-5 space-y-3">
      <p className="text-sm text-white/60">Visual groups compile to robots.txt. Validate before publishing.</p>
      <div className="flex gap-2">
        <Button size="sm" variant="ghost" className={mode === "visual" ? "text-white" : "text-white/50"} onClick={() => setMode("visual")}>Visual</Button>
        <Button size="sm" variant="ghost" className={mode === "raw" ? "text-white" : "text-white/50"} onClick={() => setMode("raw")}>Raw</Button>
      </div>
      {mode === "visual" && (
        <div className="space-y-4">
          {groups.map((g, i) => (
            <div key={i} className="rounded-lg border border-white/10 p-3 space-y-2">
              <div className="flex gap-2">
                <Input value={g.user_agent} onChange={(e) => setGroup(i, { ...g, user_agent: e.target.value })} className="bg-black/20 border-white/10 text-white" placeholder="User-agent" />
                <Button variant="ghost" className="text-white/50" onClick={() => setGroups((rows) => rows.filter((_, idx) => idx !== i))}>Remove</Button>
              </div>
              {(g.rules || []).map((r, ri) => (
                <div key={ri} className="grid grid-cols-1 sm:grid-cols-[120px_1fr_auto] gap-2">
                  <select value={r.type} onChange={(e) => {
                    const rules = [...(g.rules || [])]; rules[ri] = { ...r, type: e.target.value }; setGroup(i, { ...g, rules });
                  }} className="h-10 rounded-md bg-black/20 border border-white/10 text-white text-sm px-2">
                    <option value="allow">Allow</option>
                    <option value="disallow">Disallow</option>
                  </select>
                  <Input value={r.path} onChange={(e) => {
                    const rules = [...(g.rules || [])]; rules[ri] = { ...r, path: e.target.value }; setGroup(i, { ...g, rules });
                  }} className="bg-black/20 border-white/10 text-white" placeholder="/" />
                  <Button variant="ghost" className="text-white/50" onClick={() => setGroup(i, { ...g, rules: g.rules.filter((_, idx) => idx !== ri) })}>x</Button>
                </div>
              ))}
              <Button variant="ghost" className="text-white/70" onClick={() => setGroup(i, { ...g, rules: [...(g.rules || []), { type: "disallow", path: "/" }] })}>Add rule</Button>
            </div>
          ))}
          <Button variant="ghost" className="text-white/70" onClick={() => setGroups((g) => [...g, { user_agent: "*", rules: [{ type: "allow", path: "/" }] }])}>Add user-agent</Button>
          <Input value={sitemaps} onChange={(e) => setSitemaps(e.target.value)} className="bg-black/20 border-white/10 text-white" placeholder="Sitemap URLs, one per line" />
          <Button onClick={() => onSave({ groups, sitemaps: sitemaps.split(/\n/).map((s) => s.trim()).filter(Boolean) })} className="btn-gradient text-white border-0">Save robots.txt</Button>
        </div>
      )}
      {mode === "raw" && (
        <>
          <Textarea rows={12} value={raw} onChange={(e) => setRaw(e.target.value)} className="bg-black/40 border-white/10 text-white font-mono text-xs" />
          {(data?.issues || []).map((i) => <p key={i} className="text-xs text-amber-300">{i}</p>)}
          <Button onClick={() => onSave({ raw })} className="btn-gradient text-white border-0">Save robots.txt</Button>
        </>
      )}
    </GlassCard>
  );
}

export function SEOSitemapManager({ data, onToggle }) {
  return (
    <GlassCard className="p-5 space-y-3">
      <div className="grid grid-cols-3 gap-3 text-sm">
        <div className="text-white/60">Included <span className="text-white">{data?.included ?? 0}</span></div>
        <div className="text-white/60">Excluded <span className="text-white">{data?.excluded ?? 0}</span></div>
        <div className="text-white/40 text-xs">{data?.last_generated}</div>
      </div>
      <div className="max-h-72 overflow-auto space-y-1">
        {(data?.pages || []).map((p) => (
          <label key={p.id} className="flex items-center gap-2 text-sm text-white/70">
            <input type="checkbox" checked={p.include !== false} onChange={(e) => onToggle(p.id, e.target.checked)} />
            <span className="font-mono text-xs">{p.path}</span>
          </label>
        ))}
      </div>
    </GlassCard>
  );
}

export function SEOReports({ data, onGenerate }) {
  return (
    <GlassCard className="p-5 space-y-3">
      <p className="text-sm text-white/60">Site health {data?.site?.health ?? "—"} · {data?.pages?.length || 0} pages. Search Console metrics are omitted until connected.</p>
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => onGenerate("json")} className="btn-gradient text-white border-0">Generate JSON report</Button>
        <Button variant="ghost" className="text-white/70" onClick={() => onGenerate("csv")}>Download CSV</Button>
        <Button variant="ghost" className="text-white/70" onClick={() => onGenerate("pdf")}>Download PDF</Button>
      </div>
    </GlassCard>
  );
}

export function SEOSettingsPanel({ data, onSave, onCrawl, gsc, onConnectGsc, onDisconnectGsc, onSelectGscSite, onRefreshGsc, onCreateGscTask, onOpenGscPage, measurement, onRunCwv, cwvRunning, onRebuild, onCheckAlerts }) {
  const [form, setForm] = React.useState(data || {});
  const [showPower, setShowPower] = React.useState(false);
  React.useEffect(() => setForm(data || {}), [data]);
  const biz = form.local_business || {};
  const sites = gsc?.sites || [];
  const hookEnabled = form.deploy_hook_enabled ?? ((data?.measurement?.deploy_hook || measurement?.deploy_hook)?.enabled !== false);
  return (
    <div className="space-y-4 w-full max-w-[1600px]">
      <GlassCard className="p-5 space-y-3">
        <h3 className="text-sm text-white">Your site address</h3>
        <p className="text-xs text-white/50">Production origin Google and IndexNow should use (https://www.casmediasolutions.com).</p>
        <Input value={form.production_origin || ""} onChange={(e) => setForm({ ...form, production_origin: e.target.value })} className="bg-black/20 border-white/10 text-white" placeholder="https://www.casmediasolutions.com" />
        <Input value={biz.name || ""} onChange={(e) => setForm({ ...form, local_business: { ...biz, name: e.target.value } })} className="bg-black/20 border-white/10 text-white" placeholder="Business name" />
        <div className="flex gap-2">
          <Button onClick={() => onSave({ ...form, deploy_hook_enabled: hookEnabled })} className="btn-gradient text-white border-0">Save</Button>
        </div>
      </GlassCard>

      <GlassCard className="p-5 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-sm text-white">Google search report</h3>
            <p className="text-xs text-white/45 mt-1">
              {gsc?.connected
                ? `Connected${gsc.email ? ` as ${gsc.email}` : ""}${gsc.site_url ? ` · ${gsc.site_url}` : ""}. These rows are searches Google counted. They are not a problem list.`
                : "Connect Search Console to see which searches brought people to the site."}
            </p>
          </div>
          <div className="flex gap-2">
            {!gsc?.connected ? (
              <Button onClick={onConnectGsc} className="btn-gradient text-white border-0" disabled={!onConnectGsc}>
                Connect Google
              </Button>
            ) : (
              <>
                <Button variant="ghost" className="text-white/70" onClick={onRefreshGsc}>Refresh</Button>
                <Button variant="ghost" className="text-red-300/80" onClick={onDisconnectGsc}>Disconnect</Button>
              </>
            )}
          </div>
        </div>
        {gsc?.connected && (
          <div className="space-y-2">
            <select
              value={gsc.site_url || ""}
              onChange={(e) => onSelectGscSite?.(e.target.value)}
              className="w-full h-10 rounded-md bg-black/20 border border-white/10 text-white text-sm px-2"
            >
              <option value="">Select your verified site…</option>
              {sites.map((s) => (
                <option key={s.site_url} value={s.site_url}>{s.site_url}</option>
              ))}
            </select>
            {sites.length === 0 && (
              <p className="text-xs text-amber-100/80">
                No properties found. Verify the site in{" "}
                <a className="text-cyan-300 hover:underline" href="https://search.google.com/search-console" target="_blank" rel="noreferrer">Search Console</a>
                {" "}with this Google account, then Refresh.
              </p>
            )}
          </div>
        )}
        <SEOSearchConsole data={gsc} compact onCreateTask={onCreateGscTask} onOpenPage={onOpenGscPage} />
      </GlassCard>

      <GlassCard className="p-4">
        <button type="button" className="text-sm text-white/70 hover:text-white w-full text-left" onClick={() => setShowPower((v) => !v)}>
          {showPower ? "Hide" : "Show"} power tools (crawl schedule, speed, rebuild)…
        </button>
        {showPower && (
          <div className="mt-4 space-y-4 border-t border-white/10 pt-4">
            <div className="space-y-2">
              <p className="text-xs text-white/50">Auto-check schedule</p>
              <select value={form.audit_schedule || "weekly"} onChange={(e) => setForm({ ...form, audit_schedule: e.target.value })} className="h-10 rounded-md bg-black/20 border border-white/10 text-white text-sm px-2 w-full">
                <option value="manual">Manual only</option><option value="daily">Daily</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option>
              </select>
              {onCrawl && <Button variant="ghost" className="text-white/70" onClick={onCrawl}>Run full crawl now</Button>}
              <Button onClick={() => onSave({ ...form, deploy_hook_enabled: hookEnabled })} variant="ghost" className="text-white/70">Save schedule</Button>
            </div>
            <div className="space-y-2 text-xs text-white/60">
              <p className="text-sm text-white">Speed & rebuild</p>
              <label className="flex items-center gap-2 text-sm text-white/70">
                <input type="checkbox" checked={hookEnabled} onChange={(e) => setForm({ ...form, deploy_hook_enabled: e.target.checked })} />
                Rebuild public site when I publish SEO
              </label>
              {(() => {
                const hook = data?.measurement?.deploy_hook || measurement?.deploy_hook || {};
                const last = hook.last_result || {};
                if (last.ok === false && !last.skipped) {
                  return (
                    <p className="text-amber-200/90 text-[11px] leading-relaxed">
                      Last rebuild failed{last.status ? ` (HTTP ${last.status})` : ""}.
                      {last.hint ? ` ${last.hint}` : " Check CLOUDFLARE_PAGES_DEPLOY_HOOK_URL on the API host."}
                    </p>
                  );
                }
                if (last.skipped && last.reason === "missing_CLOUDFLARE_PAGES_DEPLOY_HOOK_URL") {
                  return (
                    <p className="text-amber-200/90 text-[11px] leading-relaxed">
                      Deploy hook URL is not configured. Set CLOUDFLARE_PAGES_DEPLOY_HOOK_URL in the API environment, then Rebuild now.
                    </p>
                  );
                }
                if (last.ok) {
                  return <p className="text-emerald-300/80 text-[11px]">Last rebuild trigger OK{last.at ? ` · ${last.at}` : ""}</p>;
                }
                return null;
              })()}
              <div className="flex flex-wrap gap-2">
                {onRunCwv && (
                  <Button variant="ghost" className="text-white/70" onClick={onRunCwv} disabled={cwvRunning}>
                    {cwvRunning ? <Loader2 className="animate-spin" /> : null}
                    {cwvRunning ? "Checking Google…" : "Speed check"}
                  </Button>
                )}
                {onRebuild && <Button variant="ghost" className="text-white/70" onClick={onRebuild}>Rebuild now</Button>}
                {onCheckAlerts && <Button variant="ghost" className="text-white/70" onClick={onCheckAlerts}>Check traffic alerts</Button>}
              </div>
            </div>
          </div>
        )}
      </GlassCard>
    </div>
  );
}

function FollowUpRow({ task, onOpenPage }) {
  const view = followUpView(task);
  return (
    <div className="flex gap-3 text-sm py-3 border-b border-white/5 items-start">
      <div className="flex-1 min-w-0">
        <p className="text-[11px] uppercase tracking-wide text-white/35">{view.kind}</p>
        <p className="text-white/90">{view.name}</p>
        {view.path && <p className="text-[11px] text-white/35">{view.path}</p>}
        <p className="text-[11px] text-white/45 mt-0.5">{view.status.detail}</p>
      </div>
      {view.status.action === "fix" && task.page_id && (
        <button type="button" className="shrink-0 text-cyan-200 text-xs underline underline-offset-2" onClick={() => onOpenPage?.(task.page_id)}>Open the title</button>
      )}
    </div>
  );
}

export function SEOIssuesTasks({ issues, tasks, onOpenPage }) {
  const openTasks = (tasks || []).filter((task) => task.status !== "done" && task.status !== "resolved");
  const fixTasks = openTasks.filter((task) => followUpView(task).status.action === "fix" && task.page_id);
  const otherTasks = openTasks.filter((task) => !(followUpView(task).status.action === "fix" && task.page_id));
  return (
    <div className="space-y-4 w-full max-w-[1600px]">
      <GlassCard className="p-5">
        <h3 className="text-sm text-white mb-1">Problems to fix</h3>
        <p className="text-xs text-white/40 mb-3">
          {(issues || []).length
            ? "Open the page, edit the field, then Save. The row leaves this list as soon as the save no longer reports it."
            : "The checker found no broken pages."}
        </p>
        {(issues || []).map((i) => (
          <div key={i.id} className="flex gap-2 text-sm text-white/70 py-2 border-b border-white/5 items-start">
            <button
              type="button"
              className="flex-1 text-left hover:text-white"
              onClick={() => i.page_id && onOpenPage?.(i.page_id)}
            >
              <span className="block text-[11px] text-cyan-200/80">{i.path || i.page_name || "No page linked"}</span>
              {i.message}
              {i.recommendation && <span className="block text-[11px] text-white/35 mt-0.5">{i.recommendation}</span>}
            </button>
          </div>
        ))}
        {(issues || []).length === 0 && <p className="text-white/40 text-sm">Nothing urgent.</p>}
      </GlassCard>
      <GlassCard className="p-5">
        <h3 className="text-sm text-white mb-1">Saved from the search report</h3>
        <p className="text-xs text-white/40 mb-3">Open the title, add the search words, then Save. The row disappears on its own. Rows that were never a real fix are removed when you open this page.</p>
        {fixTasks.length > 0 && (
          <div className="rounded-xl border border-cyan-400/30 bg-cyan-400/5 p-3 mb-4">
            <p className="text-sm text-white">Worth a title change</p>
            <p className="text-[11px] text-white/45 mb-1">Shown often, no clicks, and not your company name.</p>
            {fixTasks.map((task) => <FollowUpRow key={task.id} task={task} onOpenPage={onOpenPage} />)}
          </div>
        )}
        {otherTasks.length > 0 && (
          <div>
            <p className="text-sm text-white">Waiting to clear</p>
            <p className="text-[11px] text-white/45 mb-1">These are not title fixes. They drop off the next time this page loads.</p>
            {otherTasks.map((task) => <FollowUpRow key={task.id} task={task} onOpenPage={onOpenPage} />)}
          </div>
        )}
        {openTasks.length === 0 && <p className="text-white/40 text-sm">No saved searches.</p>}
      </GlassCard>
    </div>
  );
}

export function SEOSiteFiles({ robots, sitemap, productionOrigin, onSaveRobots, onToggleSitemap, geo, onSaveGeo, onPingIndexNow }) {
  const apiBase = (typeof window !== "undefined" && (process.env.REACT_APP_BACKEND_URL || "")) || "";
  const siteOrigin = (productionOrigin || process.env.REACT_APP_WEBSITE_URL || "").replace(/\/$/, "");
  const [llms, setLlms] = React.useState(geo?.llms_txt || "");
  const [indexEnabled, setIndexEnabled] = React.useState(geo?.indexnow?.enabled !== false);
  React.useEffect(() => {
    setLlms(geo?.llms_txt || "");
    setIndexEnabled(geo?.indexnow?.enabled !== false);
  }, [geo]);
  return (
    <div className="space-y-4 w-full max-w-[1600px]">
      <GlassCard className="p-4 text-sm text-white/60">
        Live site files (via Pages Functions, ~60s cache):{" "}
        <a className="text-cyan-300" href={`${siteOrigin}/robots.txt`} target="_blank" rel="noreferrer">/robots.txt</a>
        {" · "}
        <a className="text-cyan-300" href={`${siteOrigin}/sitemap.xml`} target="_blank" rel="noreferrer">/sitemap.xml</a>
        {" · "}
        <a className="text-cyan-300" href={`${siteOrigin}/llms.txt`} target="_blank" rel="noreferrer">/llms.txt</a>
        {" · "}
        <a className="text-cyan-300" href={`${siteOrigin}/indexnow-key.txt`} target="_blank" rel="noreferrer">/indexnow-key.txt</a>
        <p className="text-[11px] text-amber-200/70 mt-2">Page title/canonical/OG in view-source update only after a website rebuild &amp; deploy. Robots, sitemap, llms.txt, and IndexNow key are live without rebuild.</p>
      </GlassCard>
      <div className="grid lg:grid-cols-2 gap-4">
        <div>
          <h3 className="text-sm text-white mb-2">Robots.txt</h3>
          <SEORobotsManager data={robots} onSave={onSaveRobots} />
        </div>
        <div>
          <h3 className="text-sm text-white mb-2">Sitemap includes</h3>
          <SEOSitemapManager data={sitemap} onToggle={onToggleSitemap} />
        </div>
      </div>
      <GlassCard className="p-5 space-y-3">
        <h3 className="text-sm text-white">GEO — llms.txt</h3>
        <p className="text-xs text-white/45">Briefing for AI crawlers (citation-friendly). Served at /llms.txt.</p>
        <textarea
          value={llms}
          onChange={(e) => setLlms(e.target.value)}
          rows={12}
          className="w-full rounded-md bg-black/20 border border-white/10 text-white text-xs p-3 font-mono"
        />
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => onSaveGeo?.({ llms_txt: llms, indexnow_enabled: indexEnabled })}
            className="btn-gradient text-white border-0"
          >
            Save GEO files
          </Button>
        </div>
      </GlassCard>
      <GlassCard className="p-5 space-y-3">
        <h3 className="text-sm text-white">IndexNow</h3>
        <p className="text-xs text-white/45">
          On publish, URLs are submitted to IndexNow (Bing &amp; compatible engines). Key file:{" "}
          <span className="text-white/70">{geo?.indexnow?.key_location || `${siteOrigin}/indexnow-key.txt`}</span>
        </p>
        <label className="flex items-center gap-2 text-sm text-white/70">
          <input type="checkbox" checked={indexEnabled} onChange={(e) => setIndexEnabled(e.target.checked)} />
          Enabled on publish
        </label>
        {geo?.indexnow?.last_result && (
          <p className="text-[11px] text-white/40">
            Last ping: {geo.indexnow.last_result.ok ? "ok" : "failed"} · status {geo.indexnow.last_result.status ?? "—"} ·{" "}
            {geo.indexnow.last_url || "—"}
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          <Button
            variant="ghost"
            className="text-white/70"
            onClick={() => onSaveGeo?.({ llms_txt: llms, indexnow_enabled: indexEnabled })}
          >
            Save IndexNow toggle
          </Button>
          <Button variant="ghost" className="text-cyan-300/90" onClick={() => onPingIndexNow?.(["/"])}>
            Ping homepage now
          </Button>
          <Button
            variant="ghost"
            className="text-amber-200/80"
            onClick={() => onSaveGeo?.({ llms_txt: llms, indexnow_enabled: indexEnabled, regenerate_indexnow_key: true })}
          >
            Rotate IndexNow key
          </Button>
        </div>
      </GlassCard>
    </div>
  );
}

export function SEOUrlHealth({ data, performance, onRunCwv, cwvRunning }) {
  if (!data) return <GlassCard className="p-8 text-white/40">Loading URL health…</GlassCard>;
  const cwv = performance?.cwv || {};
  return (
    <div className="space-y-4 w-full max-w-[1600px]">
      <GlassCard className="p-5 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm text-white/70">
        <div>Pages {data.page_count}</div>
        <div>HTTPS {data.https_ok ? "OK" : "Issues"}</div>
        <div>Duplicate paths {(data.duplicates || []).length}</div>
      </GlassCard>

      <GlassCard className="p-5 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-sm text-white">Core Web Vitals</h3>
            <p className="text-xs text-white/45 mt-1">CrUX field data via PageSpeed Insights (not crawl TTFB).</p>
          </div>
          {onRunCwv && (
            <Button size="sm" className="btn-gradient text-white border-0" onClick={onRunCwv} disabled={cwvRunning}>
              {cwvRunning ? <Loader2 className="animate-spin" /> : null}
              {cwvRunning ? "Checking Google…" : "Run CWV check"}
            </Button>
          )}
        </div>
        {cwvRunning && (
          <p className="text-sm text-cyan-100">Checking the homepage with Google. This usually takes up to a minute. Leave this page open.</p>
        )}
        {cwv.ok ? (
          <div className={`grid sm:grid-cols-4 gap-3 text-sm text-white/75 ${cwvRunning ? "opacity-50" : ""}`}>
            <div><span className="text-white/40 text-xs block">LCP</span>{cwv.lcp_ms ?? "—"} ms</div>
            <div><span className="text-white/40 text-xs block">CLS</span>{cwv.cls ?? "—"}</div>
            <div><span className="text-white/40 text-xs block">INP</span>{cwv.inp_ms ?? "—"} ms</div>
            <div><span className="text-white/40 text-xs block">Source</span>{cwv.source} · score {cwv.performance_score ?? "—"}</div>
          </div>
        ) : !cwvRunning ? (
          <p className="text-sm text-white/50">
            {performance?.pagespeed_configured
              ? "No CWV snapshot yet — run a check for the homepage."
              : "Add PAGESPEED_API_KEY to the backend env (Google Cloud → PageSpeed Insights API), restart API, then run a check."}
          </p>
        ) : null}
        {performance?.avg_ms != null && (
          <p className="text-[11px] text-white/40">Crawl TTFB average: {performance.avg_ms} ms (separate from CWV).</p>
        )}
      </GlassCard>

      <SEOPerformance data={performance} />

      {(data.duplicates || []).map((d) => <p key={d.path} className="text-xs text-amber-300">Duplicate {d.path} × {d.count}</p>)}
      {(data.redirect_chains || []).map((c) => <p key={c.from} className="text-xs text-amber-300">Chain {c.from} → {c.to} ({c.hops} hops)</p>)}
      {(data.issues || []).map((i, idx) => <p key={idx} className="text-xs text-white/60">{i.type}: {i.message}</p>)}
      {!(data.issues || []).length && !(data.duplicates || []).length && <p className="text-sm text-white/50">No structural URL issues in the registry.</p>}
    </div>
  );
}

function RankingTable({ rows, onFix }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-[11px] text-white/45">
          <tr>
            <th className="text-left py-2 pr-3 font-medium">What it is</th>
            <th className="text-right px-3 font-medium whitespace-nowrap" title="How many people opened the site from this search">Clicks</th>
            <th className="text-right px-3 font-medium whitespace-nowrap" title="How many times Google showed the site">Shown</th>
            <th className="text-right px-3 font-medium whitespace-nowrap" title="Clicks divided by times shown">Click rate</th>
            <th className="text-right px-3 font-medium whitespace-nowrap" title="1 means the first result on Google">Place</th>
            <th className="text-right pl-3 font-medium whitespace-nowrap">What to do</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} className="border-t border-white/5 text-white/75">
              <td className="py-2 pr-3">
                <p className="text-white/90">{row.name}</p>
                {row.path && <p className="text-[11px] text-white/35 truncate max-w-[280px]">{row.path}</p>}
                <p className="text-[11px] text-white/45 mt-0.5">{row.status.detail}</p>
              </td>
              <td className="text-right align-top px-3 whitespace-nowrap">{row.clicks}</td>
              <td className="text-right align-top px-3 whitespace-nowrap">{row.impressions}</td>
              <td className="text-right align-top px-3 whitespace-nowrap">{row.ctr}%</td>
              <td className="text-right align-top px-3 whitespace-nowrap">{row.position}</td>
              <td className="text-right align-top pl-3 whitespace-nowrap">
                {row.status.action === "fix" && onFix ? (
                  <button type="button" className="text-cyan-200 text-xs underline underline-offset-2" onClick={() => onFix(row.source)}>
                    Fix the title
                  </button>
                ) : (
                  <span className={row.status.action === "keep" ? "text-emerald-200/90 text-xs" : "text-white/40 text-xs"}>{row.status.label}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RankingGroup({ title, note, rows, onFix }) {
  if (!rows.length) return null;
  const worthFixing = rows.some((row) => row.status.action === "fix");
  return (
    <div className={worthFixing ? "rounded-xl border border-cyan-400/30 bg-cyan-400/5 p-3 space-y-2" : "space-y-2"}>
      <div>
        <p className="text-sm text-white">{title}</p>
        <p className="text-[11px] text-white/45">{note}</p>
      </div>
      <RankingTable rows={rows} onFix={onFix} />
    </div>
  );
}

export function SEOSearchConsole({ data, compact, onCreateTask }) {
  const queries = (data?.queries || []).map((row) => ({
    key: row.query,
    name: row.query,
    path: "",
    clicks: row.clicks,
    impressions: row.impressions,
    ctr: row.ctr,
    position: row.position,
    status: searchRowStatus(row),
    source: row,
  }));
  const pages = (data?.pages || []).slice(0, 10).map((row) => {
    let path = row.page;
    try { path = new URL(row.page).pathname || "/"; } catch { /* keep the raw value */ }
    return {
      key: row.page,
      name: pageDisplayName(row.page),
      path,
      clicks: row.clicks,
      impressions: row.impressions,
      ctr: row.impressions ? Math.round((Number(row.clicks || 0) / Number(row.impressions)) * 1000) / 10 : 0,
      position: row.position,
      status: pageRowStatus(row),
      source: row,
    };
  });
  const fixQueries = queries.filter((row) => row.status.action === "fix");
  const keepQueries = queries.filter((row) => row.status.action === "keep");
  const waitQueries = queries.filter((row) => row.status.action === "wait");
  const fixPages = pages.filter((row) => row.status.action === "fix");
  const keepPages = pages.filter((row) => row.status.action === "keep");
  const waitPages = pages.filter((row) => row.status.action === "wait");
  const openFix = (kind) => (source) => {
    if (kind === "query") onCreateTask?.({ kind: "query", ...source });
    else onCreateTask?.({ kind: "page", page: source.page, ...source });
  };

  return (
    <GlassCard className={compact ? "p-4 space-y-4 border-white/5 bg-black/10" : "p-6 space-y-4"}>
      {!compact && (
        <>
          <p className="text-white">{data?.connected ? "Connected" : "Not connected"}</p>
          <p className="text-sm text-white/60">{data?.note}</p>
        </>
      )}
      {compact && data?.note && !data?.connected && (
        <p className="text-sm text-white/50">{data.note}</p>
      )}
      {data?.error && data?.note && (
        <p className="text-xs text-amber-200/80">{data.note}</p>
      )}
      {queries.length === 0 && (
        <p className="text-sm text-white/45 py-4 text-center">
          {data?.connected
            ? (data?.site_url ? "No searches for the last 28 days yet." : "Select a property above to load the report.")
            : "The report stays empty until Search Console is connected."}
        </p>
      )}
      <RankingGroup
        title="Searches worth a title change"
        note="Shown at least 10 times, no clicks, and not your company name. This is the only group with a fix button."
        rows={fixQueries}
        onFix={onCreateTask ? openFix("query") : null}
      />
      <RankingGroup
        title="Searches that already work"
        note="Your company name, or a search that already gets clicks near the top. Do not change these titles."
        rows={keepQueries}
      />
      <RankingGroup
        title="Searches with too little data"
        note="Google showed these once or only a few times. They are real searches, not problems, and they cannot be fixed from here."
        rows={waitQueries}
      />
      {(pages.length > 0) && (
        <div className="space-y-4 pt-2">
          <RankingGroup
            title="Pages worth a title change"
            note="Shown at least 20 times, no clicks, and far down the results. Open the title, match the search, then Save and Publish."
            rows={fixPages}
            onFix={onCreateTask ? openFix("page") : null}
          />
          <RankingGroup
            title="Pages that already get clicks"
            note="People already open these from Google. Leave the titles as they are."
            rows={keepPages}
          />
          <RankingGroup
            title="Pages with too little data"
            note="Google only showed these a handful of times. Wait. There is no title change that will help yet."
            rows={waitPages}
          />
        </div>
      )}
    </GlassCard>
  );
}

export function SEOPerformance({ data }) {
  return (
    <GlassCard className="p-5 space-y-3">
      <p className="text-sm text-white/60">Crawl response times (TTFB from last analyze — separate from Core Web Vitals above).</p>
      <p className="text-sm text-white/70">Average response {data?.avg_ms ?? "—"} ms</p>
      {(data?.slowest || []).map((r) => <div key={r.path} className="text-sm text-white/70 flex justify-between"><span className="font-mono text-xs">{r.path}</span><span>{r.ms} ms · {r.status}</span></div>)}
      {!(data?.slowest || []).length && <p className="text-white/40 text-sm">Analyze pages to collect timings.</p>}
    </GlassCard>
  );
}

export function SEOCrawlHistory({ items }) {
  return (
    <GlassCard className="p-5 space-y-2">
      {(items || []).map((j) => (
        <div key={j.id} className="text-sm text-white/70 border-b border-white/5 py-2 flex justify-between">
          <span>{j.type || "crawl"} · {j.status}</span>
          <span className="text-white/40">{j.done}/{j.total} · {(j.created_at || "").slice(0, 16)}</span>
        </div>
      ))}
      {!(items || []).length && <p className="text-white/40 text-sm">No crawl jobs yet.</p>}
    </GlassCard>
  );
}

export function SEOSchemaStudio({ templates, page, onChange, onValidate }) {
  const sample = templates?.sample || {};
  const names = templates?.templates || Object.keys(sample);
  const [json, setJson] = React.useState(typeof page?.schema === "object" ? (page.schema.configured || "") : "");
  const [result, setResult] = React.useState(null);
  React.useEffect(() => {
    setJson(typeof page?.schema === "object" ? (page.schema.configured || "") : (page?.schema || ""));
  }, [page]);
  return (
    <div className="grid lg:grid-cols-[240px_1fr] gap-4">
      <GlassCard className="p-4 space-y-1">
        {names.map((n) => (
          <button key={n} className="block w-full text-left text-sm text-white/70 hover:text-white py-1" onClick={() => setJson(JSON.stringify(sample[n] || { "@type": n }, null, 2))}>{n}</button>
        ))}
      </GlassCard>
      <GlassCard className="p-5 space-y-3">
        <Textarea rows={16} value={json} onChange={(e) => setJson(e.target.value)} className="bg-black/40 border-white/10 text-white font-mono text-xs" />
        <div className="flex gap-2">
          <Button variant="ghost" className="text-white/70" onClick={async () => setResult(await onValidate(json))}>Validate</Button>
          {page && onChange && <Button className="btn-gradient text-white border-0" onClick={() => onChange({ ...page, schema: { ...(page.schema || {}), configured: json } })}>Apply to current page</Button>}
        </div>
        {result && <p className={`text-sm ${result.ok ? "text-emerald-300" : "text-amber-300"}`}>{result.ok ? "Valid JSON-LD" : (result.errors || []).join(" ")}</p>}
        {!page && <p className="text-xs text-white/40">Open a page from SEO Pages to apply schema as a draft.</p>}
      </GlassCard>
    </div>
  );
}
